/**
 * The states of the particle orb and how it moves between them: each state has a weight that eases
 * towards 1 (the current one) or 0 (the others), and every drawing parameter is the weighted mix of
 * the states' tables, so a change of state is a glide, never a jump.
 * From the "Particles Orb" component (lib/orb-state.ts), reduced to what the kiosk uses.
 */

export type OrbState = 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'error' | 'disabled';

export type StateWeights = Record<OrbState, number>;

const STATES: readonly OrbState[] = ['idle', 'connecting', 'listening', 'thinking', 'speaking', 'error', 'disabled'];

const wave = (x: number): number => 0.5 - 0.5 * Math.cos(x);

/** How lively a state is at time t (0..1), when no live audio level drives the orb. */
export function stateEnergy(state: OrbState, t: number): number {
  switch (state) {
    case 'listening': return 0.4 + 0.32 * wave(t * 17) + 0.18 * wave(t * 8.2 + 3);
    case 'speaking': return 0.3 + 0.24 * wave(t * 12.4) + 0.16 * wave(t * 6 + 1.2);
    case 'thinking': return 0.24 + 0.2 * wave(t * 4.8);
    case 'connecting': return 0.12 + 0.1 * wave(t * 3.2);
    case 'error': return 0.2;
    default: return 0;
  }
}

/** Exponential easing of `current` towards `target` over dt seconds. */
export const approach = (current: number, target: number, rate: number, dt: number): number =>
  current + (target - current) * (1 - Math.exp(-rate * dt));

/** Active states arrive in about 0.2 s, idle settles in about 0.6 s. */
function stateRate(state: OrbState): number {
  if (state === 'idle' || state === 'disabled') return 5;
  if (state === 'error') return 10;
  return 14;
}

export interface StateMix {
  weights: StateWeights;
  update(state: OrbState, dt: number): StateWeights;
}

export function createStateMix(initial: OrbState = 'idle'): StateMix {
  const weights = Object.fromEntries(STATES.map((s) => [s, s === initial ? 1 : 0])) as StateWeights;
  return {
    weights,
    update(state, dt) {
      const rate = stateRate(state);
      let total = 0;
      for (const key of STATES) {
        const target = key === state ? 1 : 0;
        const next = approach(weights[key], target, rate, dt);
        weights[key] = target === 0 && next < 0.001 ? 0 : next;
        total += weights[key];
      }
      if (total > 0) for (const key of STATES) weights[key] /= total;
      return weights;
    },
  };
}

/** Every parameter of the table, mixed by the states' weights. */
export function blendStates<T extends Record<string, number>>(weights: StateWeights, table: Record<OrbState, T>): T {
  const out: Record<string, number> = {};
  for (const key of STATES) {
    const w = weights[key];
    if (w === 0) continue;
    for (const [param, value] of Object.entries(table[key])) out[param] = (out[param] ?? 0) + value * w;
  }
  return out as T;
}

export function blendEnergy(weights: StateWeights, t: number): number {
  let energy = 0;
  for (const key of STATES) if (weights[key] > 0) energy += weights[key] * stateEnergy(key, t);
  return energy;
}

export const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));
